import { useAuth } from '@clerk/clerk-react'
import { useQuery, type Query } from '@tanstack/react-query'
import type { MicrosoftConnectionStatus } from '../api/contracts'
import { getMicrosoftConnection } from '../api/integrations.api'
import { integrationsQueryKeys } from '../api/queryKeys'

/** How often a connection being set up in the background is read again. */
export const MICROSOFT_SETUP_POLL_MS = 3000

interface MicrosoftConnectionStatusOptions {
  enabled?: boolean
  /** How long a read stays current for this reader. */
  staleTime?: number
  /**
   * Read again when the window regains focus: where the connection is shown
   * or used (Credentials, the email step's sender). The navigation only asks
   * whether Microsoft 365 is configured, which doesn't change in a session.
   */
  refetchOnWindowFocus?: boolean
  /** Read only while the cached connection is being set up (the app shell's watcher). */
  onlyWhileConnecting?: boolean
}

export function isMicrosoftSetupRunning(connection: MicrosoftConnectionStatus | undefined) {
  return connection?.setup_status === 'connecting'
}

/**
 * Lightweight local connection status; this does not run an Exchange probe.
 * It's read where it's needed, while that's on screen, and every few seconds
 * while Microsoft 365 is connecting in the background.
 */
export function useMicrosoftConnectionStatus({
  enabled = true,
  staleTime = 60_000,
  refetchOnWindowFocus = true,
  onlyWhileConnecting = false,
}: MicrosoftConnectionStatusOptions = {}) {
  const { userId, orgId, isLoaded } = useAuth()
  const signedIn = enabled && isLoaded && !!userId && !!orgId
  return useQuery({
    queryKey: integrationsQueryKeys.microsoftConnection(userId, orgId),
    queryFn: ({ signal }) => getMicrosoftConnection(signal),
    enabled: onlyWhileConnecting
      ? (query: Query<MicrosoftConnectionStatus>) =>
          signedIn && isMicrosoftSetupRunning(query.state.data)
      : signedIn,
    staleTime,
    refetchOnWindowFocus,
    refetchInterval: (query) =>
      isMicrosoftSetupRunning(query.state.data) ? MICROSOFT_SETUP_POLL_MS : false,
  })
}
